import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TopTenantsTable = ({ tenants, loading = false }) => {
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const sortedTenants = [...tenants].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'trial': return 'text-warning bg-warning/10';
      case 'expired': return 'text-error bg-error/10';
      case 'suspended': return 'text-text-secondary bg-muted/20';
      default: return 'text-text-secondary bg-muted/20';
    }
  };

  const getRenewalUrgency = (daysUntilRenewal) => {
    if (daysUntilRenewal <= 7) return 'text-error';
    if (daysUntilRenewal <= 30) return 'text-warning';
    return 'text-success';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-text-secondary" />;
    }
    return (
      <Icon 
        name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={14} 
        className="text-primary" 
      />
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-muted rounded w-40"></div>
            <div className="h-8 bg-muted rounded w-24"></div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center space-x-4 p-3 bg-muted/20 rounded-lg">
                <div className="h-10 w-10 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-32"></div>
                  <div className="h-3 bg-muted rounded w-24"></div>
                </div>
                <div className="h-4 bg-muted rounded w-16"></div>
                <div className="h-4 bg-muted rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Top Performing Tenants</h2>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm">
            <Icon name="Filter" size={16} className="mr-2" />
            Filter
          </Button>
          <Button variant="ghost" size="sm">
            <Icon name="Download" size={16} />
          </Button>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  <span>Tenant</span>
                  <SortIcon column="name" />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('revenue')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  <span>MRR</span>
                  <SortIcon column="revenue" />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('users')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  <span>Users</span>
                  <SortIcon column="users" />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <button
                  onClick={() => handleSort('usage')}
                  className="flex items-center space-x-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  <span>Usage</span>
                  <SortIcon column="usage" />
                </button>
              </th>
              <th className="text-left py-3 px-2">
                <span className="text-sm font-medium text-text-secondary">Status</span>
              </th>
              <th className="text-left py-3 px-2">
                <span className="text-sm font-medium text-text-secondary">Renewal</span>
              </th>
              <th className="text-right py-3 px-2">
                <span className="text-sm font-medium text-text-secondary">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTenants.map((tenant) => (
              <tr 
                key={tenant.id} 
                className="border-b border-border hover:bg-muted/20 transition-colors cursor-pointer"
              >
                <td className="py-4 px-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {tenant.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-text-primary">{tenant.name}</p>
                      <p className="text-xs text-text-secondary">{tenant.plan}</p>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <div>
                    <p className="font-semibold text-text-primary">
                      {formatCurrency(tenant.revenue)}
                    </p>
                    <p className="text-xs text-success">
                      +{tenant.revenueGrowth}% MoM
                    </p>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <div>
                    <p className="font-medium text-text-primary">{tenant.users.toLocaleString()}</p>
                    <p className="text-xs text-text-secondary">
                      +{tenant.userGrowth} this month
                    </p>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary rounded-full h-2 transition-all duration-300"
                        style={{ width: `${tenant.usage}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-text-primary">
                      {tenant.usage}%
                    </span>
                  </div>
                </td>
                <td className="py-4 px-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tenant.status)}`}>
                    {tenant.status.charAt(0).toUpperCase() + tenant.status.slice(1)}
                  </span>
                </td>
                <td className="py-4 px-2">
                  <div>
                    <p className={`text-sm font-medium ${getRenewalUrgency(tenant.renewalDays)}`}>
                      {tenant.renewalDays} days
                    </p>
                    <p className="text-xs text-text-secondary">
                      {new Date(tenant.renewalDate).toLocaleDateString()}
                    </p>
                  </div>
                </td>
                <td className="py-4 px-2 text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <Button variant="ghost" size="sm">
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="MessageSquare" size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="MoreHorizontal" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Showing {sortedTenants.length} of {sortedTenants.length} tenants
        </p>
        <Button variant="ghost" size="sm">
          View All Tenants
          <Icon name="ArrowRight" size={14} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default TopTenantsTable;